import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
  Switch,
  LinearProgress,
} from "@mui/material";
import {
  CheckCircle,
  Lock,
  Camera,
  UploadCloud,
  FileText,
  Building2,
  Mail,
  ShieldCheck,
  Briefcase,
  AlertCircle,
  Check,
  Sun,
  Moon,
  Eye,
  EyeOff,
  UserCircle2,
  KeyRound,
  Users,
  Settings2,
  LayoutDashboard,
  Info
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import packageJson from "../../../package.json";
import UseOnlineStatus from "../../common/UseOnlineStatus";
import NoInternet from "../../common/NoInternet";

/* ---------------------------------------------------------
   Animation helpers
--------------------------------------------------------- */
const FadeInUp = ({ children, delay = 0, className = "" }) => (
  <div
    className={`animate-[fadeInUp_0.6s_ease_forwards] opacity-0 ${className}`}
    style={{ animationDelay: `${delay}ms` }}
  >
    {children}
  </div>
);

const StaggerContainer = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

/* ---------------------------------------------------------
   Theme tokens
--------------------------------------------------------- */
const themes = {
  dark: {
    bg: "#0d1117",
    panel: "#161b22",
    panelAlt: "#0d1117",
    border: "border-slate-800",
    borderInput: "border-slate-700",
    text: "text-slate-100",
    textMuted: "text-slate-400",
    textFaint: "text-slate-500",
  },
  light: {
    bg: "#f5f6fa",
    panel: "#ffffff",
    panelAlt: "#f5f6fa",
    border: "border-slate-200",
    borderInput: "border-slate-300",
    text: "text-slate-900",
    textMuted: "text-slate-600",
    textFaint: "text-slate-400",
  },
};

export default function UserProfile() {
  const navigate = useNavigate();
  const { isOnline } = UseOnlineStatus();

  const [mode, setMode] = useState("dark");
  const t = themes[mode];
  const isDark = mode === "dark";

  const [formData, setFormData] = useState({
    fullName: "",
    preferredName: "",
    employeeId: "",
    workEmail: "",
    personalEmail: "",
    phone: "",
    jobTitle: "",
    department: "",
    fleetHub: "",
    joinDate: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyRelation: "",
    gender: "",
    dob: "",
    username: "",
    password: "",
    modeOfTransport: "",
    idProof: "",
    otherInfo: "",
  });

  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [usernameStatus, setUsernameStatus] = useState("idle"); // idle | checking | available | taken
  const [phoneStatus, setPhoneStatus] = useState("idle"); // idle | checking | available | taken
  const [uploadProgress, setUploadProgress] = useState(0);

  const [accessInfo, setAccessInfo] = useState({
    designation: "",
    level: "",
    roles: [],
    actions: [],
  });

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [documents, setDocuments] = useState({
    drivingLicense: null,
    aadhaarPan: null,
    resumeAgreement: null,
  });

  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bannerMessage, setBannerMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    const savedProfile = localStorage.getItem("profile");
    if (savedProfile) {
      try {
        const parsed = JSON.parse(savedProfile);
        if (parsed.isProfileSubmitted) {
          setFormData(parsed);
          setAvatarPreview(parsed.avatarUrl || null);
          setIsLocked(true);
        }
      } catch (e) {
        console.error("Local profile parse error", e);
      }
    }

    const savedTheme = localStorage.getItem("profileTheme");
    if (savedTheme === "light" || savedTheme === "dark") {
      setMode(savedTheme);
    }

    const verifyRemoteProfile = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await fetch("/api/employee/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.isProfileComplete) {
            setFormData(data.profileData);
            setAvatarPreview(data.profileData.avatarUrl || null);
            setIsLocked(true);
            if (data.accessInfo) setAccessInfo(data.accessInfo);
            localStorage.setItem(
              "profile",
              JSON.stringify({ ...data.profileData, isProfileSubmitted: true })
            );
          }
        }
      } catch (error) {
        console.error("Profile check error:", error);
      } finally {
        setLoading(false);
      }
    };

    verifyRemoteProfile();
  }, []);

  // Toast auto-dismiss
  useEffect(() => {
    if (!bannerMessage.text) return;
    const timer = setTimeout(() => setBannerMessage({ type: "", text: "" }), 5000);
    return () => clearTimeout(timer);
  }, [bannerMessage.text]);

  // Debounced username availability check
  useEffect(() => {
    if (isLocked || !formData.username || formData.username.length < 3) {
      setUsernameStatus("idle");
      return;
    }
    setUsernameStatus("checking");
    const handle = setTimeout(async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `/api/employee/username-available?username=${encodeURIComponent(formData.username)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setUsernameStatus(data.available ? "available" : "taken");
        } else {
          setUsernameStatus("idle");
        }
      } catch {
        const taken = ["admin", "rahul.sharma", "test"].includes(
          formData.username.toLowerCase()
        );
        setUsernameStatus(taken ? "taken" : "available");
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [formData.username, isLocked]);

  // Debounced Mobile Uniqueness check
  useEffect(() => {
    if (isLocked || !formData.phone || formData.phone.length < 10) {
      setPhoneStatus("idle");
      return;
    }
    setPhoneStatus("checking");
    const handle = setTimeout(async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `/api/employee/mobile-available?phone=${encodeURIComponent(formData.phone)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setPhoneStatus(data.available ? "available" : "taken");
        } else {
          setPhoneStatus("idle");
        }
      } catch {
        const taken = ["+91 98765 43210", "9876543210"].includes(formData.phone);
        setPhoneStatus(taken ? "taken" : "available");
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [formData.phone, isLocked]);

  const validateField = (name, value) => {
    let message = "";
    switch (name) {
      case "fullName":
        if (!value.trim()) message = "Full name is required.";
        else if (value.trim().length < 2) message = "Name looks too short.";
        break;
      case "workEmail":
      case "personalEmail":
        if (!value.trim()) message = "Email is required.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) message = "Enter a valid email address.";
        break;
      case "phone":
      case "emergencyContactPhone":
        if (!value.trim()) message = "Phone number is required.";
        else if (!/^[+]?[\d\s-]{10,15}$/.test(value)) message = "Enter a valid phone number.";
        break;
      case "username":
        if (!value.trim()) message = "Username is required.";
        else if (!/^[a-zA-Z0-9._-]{3,32}$/.test(value))
          message = "3-32 characters — letters, numbers, dots, hyphens only.";
        break;
      case "password":
        if (!value) message = "Password is required.";
        else if (value.length < 8) message = "Use at least 8 characters.";
        break;
      case "confirmPassword":
        if (value !== formData.password) message = "Passwords don't match.";
        break;
      case "dob": {
        if (!value) message = "Date of birth is required.";
        else {
          const age = (Date.now() - new Date(value).getTime()) / (1000 * 60 * 60 * 24 * 365.25);
          if (age < 18) message = "Employee must be at least 18 years old.";
          if (age > 80) message = "Please double-check the date of birth.";
        }
        break;
      }
      default:
        break;
    }
    return message;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const message = validateField(name, value);
    setFieldErrors((prev) => ({ ...prev, [name]: message }));
  };

  const passwordStrength = (() => {
    const p = formData.password || "";
    let score = 0;
    if (p.length >= 8) score++;
    if (p.length >= 12) score++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
    if (/\d/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    const labels = ["Very weak", "Weak", "Fair", "Good", "Strong", "Very strong"];
    const colors = ["bg-rose-500", "bg-rose-500", "bg-amber-500", "bg-amber-400", "bg-emerald-500", "bg-emerald-500"];
    return { score, label: p ? labels[score] : "", color: colors[score] };
  })();

  const sectionStatus = {
    identity: ["fullName", "employeeId", "jobTitle", "department", "fleetHub", "joinDate"].every(
      (k) => formData[k]?.toString().trim()
    ),
    contact: ["workEmail", "phone", "emergencyContactName"].every((k) => formData[k]?.toString().trim()),
    credentials:
      ["gender", "dob", "personalEmail", "username", "password"].every((k) => formData[k]?.toString().trim()) &&
      formData.password === confirmPassword,
    compliance: ["modeOfTransport", "idProof"].every((k) => formData[k]?.toString().trim()),
    documents: Object.values(documents).some(Boolean),
  };

  const completedSectionsCount = Object.values(sectionStatus).filter(Boolean).length;
  const totalSections = Object.keys(sectionStatus).length;

  const simulateSavedProfile = () => {
    const sample = {
      fullName: "Rahul Sharma",
      preferredName: "Rahul",
      employeeId: "WC-8492",
      workEmail: "rahul.sharma@wowcarz.in",
      personalEmail: "rahul.s@gmail.com",
      phone: "+91 98765 43210",
      jobTitle: "Fleet Operations Engineer",
      department: "Self-Drive Operations",
      fleetHub: "Bengaluru Central Station",
      joinDate: "2025-03-10",
      emergencyContactName: "Anita Sharma",
      emergencyContactPhone: "+91 91234 56789",
      emergencyRelation: "Sister",
      gender: "Male",
      dob: "1998-07-22",
      username: "rahul.sharma",
      password: "",
      modeOfTransport: "Own Vehicle",
      idProof: "Aadhaar Card",
      otherInfo: "Ready for immediate deployment.",
      avatarUrl: null,
      isProfileSubmitted: true,
    };
    localStorage.setItem("profile", JSON.stringify(sample));
    setFormData(sample);
    setAvatarPreview(sample.avatarUrl);
    setIsLocked(true);
    setAccessInfo({
      designation: "Fleet Operations Engineer",
      level: "L2",
      roles: ["Agent", "Sales Agent"],
      actions: [
        "View Bookings",
        "Update Vehicle Status",
        "Approve Refunds",
        "Access Fleet Dashboard",
      ],
    });
    setBannerMessage({
      type: "success",
      text: "Loaded sample saved profile from localStorage (dev preview).",
    });
  };

  const clearSavedProfile = () => {
    localStorage.removeItem("profile");
    setIsLocked(false);
    setFormData({
      fullName: "",
      preferredName: "",
      employeeId: "",
      workEmail: "",
      personalEmail: "",
      phone: "",
      jobTitle: "",
      department: "",
      fleetHub: "",
      joinDate: "",
      emergencyContactName: "",
      emergencyContactPhone: "",
      emergencyRelation: "",
      gender: "",
      dob: "",
      username: "",
      password: "",
      modeOfTransport: "",
      idProof: "",
      otherInfo: "",
    });
    setConfirmPassword("");
    setAvatarPreview(null);
    setAccessInfo({ designation: "", level: "", roles: [], actions: [] });
    setBannerMessage({ type: "", text: "" });
  };

  const toggleTheme = () => {
    const next = mode === "dark" ? "light" : "dark";
    setMode(next);
    localStorage.setItem("profileTheme", next);
  };

  const handleInputChange = (e) => {
    if (isLocked) return;
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAvatarChange = (e) => {
    if (isLocked) return;
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
    }
  };

  const handleDocumentChange = (key, e) => {
    if (isLocked) return;
    const file = e.target.files[0];
    if (file) {
      setDocuments((prev) => ({ ...prev, [key]: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLocked) return;
    if (formData.password !== confirmPassword) {
      setFieldErrors(prev => ({...prev, confirmPassword: "Passwords do not match."}));
      return;
    }

    setSubmitting(true);
    setUploadProgress(0);
    setBannerMessage({ type: "", text: "" });

    // Mock progress interval since native fetch doesn't track upload progress
    const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
            if (prev >= 90) {
                clearInterval(progressInterval);
                return 90;
            }
            return prev + 15;
        });
    }, 300);

    try {
      const token = localStorage.getItem("token");
      const dataPayload = new FormData();
      Object.keys(formData).forEach((key) => {
        dataPayload.append(key, formData[key]);
      });

      if (documents.drivingLicense) dataPayload.append("drivingLicense", documents.drivingLicense);
      if (documents.aadhaarPan) dataPayload.append("aadhaarPan", documents.aadhaarPan);
      if (documents.resumeAgreement) dataPayload.append("resumeAgreement", documents.resumeAgreement);

      const response = await fetch("/api/employee/profile/create", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: dataPayload,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        setIsLocked(true);
        localStorage.setItem(
          "profile",
          JSON.stringify({ ...formData, avatarUrl: avatarPreview, isProfileSubmitted: true })
        );
        setBannerMessage({
          type: "success",
          text: "Profile identity and onboarding documents successfully submitted and locked.",
        });
      } else {
        const errorData = await response.json();
        setBannerMessage({
          type: "error",
          text: errorData.message || "Failed to submit profile. Please verify your fields.",
        });
      }
    } catch (err) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      setBannerMessage({
        type: "error",
        text: "Network error occurred while submitting your employee identity.",
      });
    } finally {
      setSubmitting(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  const getInputClass = (name) => {
    const base = `w-full ${isDark ? "bg-[#0d1117]" : "bg-slate-50"} border rounded-lg px-3.5 py-2.5 text-sm ${t.text} focus:outline-none focus:ring-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200`;
    if (fieldErrors[name]) {
        return `${base} border-rose-500 focus:border-rose-500 focus:ring-rose-500/20`;
    }
    return `${base} ${t.borderInput} focus:border-indigo-500 focus:ring-indigo-500/20`;
  };

  if (!isOnline) {
    return (
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isDark ? "#0d1117" : "rgba(255,255,255,0.98)",
        }}
      >
        <NoInternet isOnline={isOnline} />
      </Box>
    );
  }

  if (loading) {
    return (
      <div
        className="flex justify-center items-center min-h-screen"
        style={{ background: t.bg }}
      >
        <div className="flex flex-col items-center gap-3">
          <CircularProgress className="text-indigo-500" />
          <span className={`text-xs ${t.textFaint} animate-pulse`}>Loading employee record…</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${t.text} font-sans transition-colors duration-300 relative pb-24`}
      style={{ background: t.bg }}
    >
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes floaty {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        .card-hover {
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .card-hover:hover {
          transform: translateY(-2px);
          border-color: rgba(99,102,241,0.5);
        }
      `}</style>

      {/* ============ TOPBAR (MATCHES LAYOUT.JSX APPBAR) ============ */}
      <header
        className="sticky top-0 z-50 px-4 sm:px-6 py-2.5 flex items-center justify-between"
        style={{
          background: "linear-gradient(135deg, rgba(17,24,39,0.95), rgba(30,41,59,0.92))",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 12px 30px rgba(15,23,42,0.18)",
        }}
      >
        <div
          className="flex items-center gap-3 cursor-pointer select-none transition-transform hover:scale-[1.02]"
          onClick={() => navigate("/dashboard")}
          title="Return to Main Dashboard"
        >
          <img
            src="https://wowcarz-file-upload.s3.ap-south-1.amazonaws.com/undefined/images-1694673789495-wowcarzlogo.png"
            alt="Wowcarz"
            className="h-10 object-contain"
          />
          <span className="hidden sm:inline-block text-[11px] font-semibold tracking-wider text-indigo-400 bg-indigo-950/60 border border-indigo-800/80 px-2 py-0.5 rounded-md uppercase">
            Employee Portal
          </span>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-slate-300 bg-white/5 hover:bg-white/15 border border-white/10 hover:border-white/25 transition-all duration-200"
          >
            <LayoutDashboard className="w-3.5 h-3.5 text-indigo-400" />
            <span>Dashboard</span>
          </button>

          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full shadow-sm transition-all duration-500 ease-out ${
              isOnline
                ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                : "bg-red-500/20 text-red-300 border border-red-500/30"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isOnline ? "bg-emerald-400" : "bg-red-400 animate-pulse"
              }`}
            />
            <span className="text-xs font-medium">
              {isOnline ? "Online" : "Offline"}
            </span>
          </div>

          <Tooltip title={isDark ? "Switch to light theme" : "Switch to dark theme"}>
            <button
              onClick={toggleTheme}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
            >
              <Sun className={`w-3.5 h-3.5 ${isDark ? "text-slate-400" : "text-amber-400"}`} />
              <Switch
                size="small"
                checked={isDark}
                onChange={toggleTheme}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#818cf8" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4338ca" },
                }}
              />
              <Moon className={`w-3.5 h-3.5 ${isDark ? "text-indigo-400" : "text-slate-400"}`} />
            </button>
          </Tooltip>

          <div className="hidden sm:flex items-center px-2.5 py-1 rounded-full bg-white/10 border border-white/10 text-white text-xs font-mono font-semibold">
            {"v" + packageJson?.version}
          </div>

          <div
            className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 cursor-default"
          >
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontWeight: 700,
                fontSize: 12,
                background: "linear-gradient(135deg,#6366F1,#8B5CF6,#EC4899)",
                boxShadow: "0 4px 12px rgba(99,102,241,.45)",
              }}
            >
              {formData.fullName ? formData.fullName.charAt(0).toUpperCase() : "U"}
            </Avatar>
            <span className="hidden sm:inline text-xs font-semibold text-white truncate max-w-[120px]">
              {formData.preferredName || formData.fullName || "Employee"}
            </span>
            {isLocked && <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />}
          </div>
        </div>
      </header>

      {/* Progress Stepper */}
      {!isLocked && (
        <div className={`sticky top-[61px] z-40 w-full px-4 py-2 text-xs font-medium border-b flex justify-center items-center gap-4 ${isDark ? 'bg-[#0d1117]/90 border-slate-800 text-slate-300' : 'bg-white/90 border-slate-200 text-slate-600'} backdrop-blur-md`}>
            <span>Form Progress: {completedSectionsCount} of {totalSections} sections complete</span>
            <div className="w-48 h-1.5 rounded-full bg-slate-200/20 overflow-hidden">
                <div 
                    className="h-full bg-indigo-500 transition-all duration-500" 
                    style={{ width: `${(completedSectionsCount / totalSections) * 100}%` }}
                />
            </div>
        </div>
      )}

      {/* Top Banner Gradient */}
      <div className="h-44 w-full bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20 backdrop-blur-[1px]" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.15) 45%, transparent 60%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 4s linear infinite",
          }}
        />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="flex justify-end gap-2 mb-3 relative z-20">
          <button
            type="button"
            onClick={simulateSavedProfile}
            className="text-[11px] px-3 py-1.5 rounded-md bg-indigo-600/90 hover:bg-indigo-500 text-white shadow-md transition"
          >
            ▶ Simulate saved profile
          </button>
          <button
            type="button"
            onClick={clearSavedProfile}
            className={`text-[11px] px-3 py-1.5 rounded-md border ${t.borderInput} ${isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-200" : "bg-white hover:bg-slate-100 text-slate-700"} shadow-md transition`}
          >
            ✕ Clear
          </button>
        </div>

        {/* Header Profile Card */}
        <FadeInUp>
          <div
            className={`${isDark ? "bg-[#161b22]" : "bg-white"} border ${t.border} rounded-2xl p-6 shadow-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-6 card-hover`}
          >
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="relative group" style={{ animation: "floaty 4s ease-in-out infinite" }}>
                <Avatar
                  src={avatarPreview}
                  className="w-28 h-28 border-4 bg-slate-800 text-3xl font-bold ring-2 ring-indigo-500/50"
                  sx={{ borderColor: isDark ? "#161b22" : "#ffffff" }}
                >
                  {formData.fullName ? formData.fullName.charAt(0) : "W"}
                </Avatar>
                {!isLocked && (
                  <label className="absolute bottom-1 right-1 p-2 bg-indigo-600 rounded-full cursor-pointer hover:bg-indigo-500 hover:scale-110 transition shadow-lg">
                    <Camera className="w-4 h-4 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarChange}
                    />
                  </label>
                )}
              </div>

              <div className="text-center sm:text-left space-y-1">
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <h1 className="text-2xl font-bold tracking-tight">
                    {formData.fullName || "New WOWCARZ Team Member"}
                  </h1>
                  {isLocked ? (
                    <Tooltip title="Verified Permanent Employee Record">
                      <CheckCircle className="w-5 h-5 text-emerald-400 fill-emerald-400/20" />
                    </Tooltip>
                  ) : (
                    <span className="text-xs bg-amber-500/10 text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-full font-medium">
                      Draft • Unsubmitted
                    </span>
                  )}
                </div>
                <p className={`${t.textMuted} text-sm flex items-center gap-2 justify-center sm:justify-start`}>
                  <Briefcase className="w-4 h-4 text-slate-500" />
                  {formData.jobTitle || "Fleet Operations / Engineering"} • {formData.department || "Self-Drive Division"}
                </p>
                <div className="flex items-center gap-2 pt-1 justify-center sm:justify-start">
                  <Chip
                    label={formData.employeeId ? `ID: ${formData.employeeId}` : "ID: PENDING"}
                    size="small"
                    className={`${isDark ? "bg-slate-800 text-slate-300 border-slate-700" : "bg-slate-100 text-slate-600 border-slate-300"} font-mono text-xs border`}
                  />
                  <Chip
                    label="WOWCARZ Internal Hub"
                    size="small"
                    className="bg-indigo-950 text-indigo-300 border border-indigo-800 text-xs font-semibold"
                  />
                </div>
              </div>
            </div>

            <div className="w-full md:w-auto flex flex-col items-center md:items-end gap-2 border-t md:border-t-0 pt-4 md:pt-0 border-slate-800">
              {isLocked ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-950/40 border border-emerald-800/60 rounded-xl text-emerald-300 text-xs font-medium">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  Immutable Production Record
                </div>
              ) : (
                <div className="flex items-center gap-2 px-4 py-2 bg-amber-950/40 border border-amber-800/60 rounded-xl text-amber-300 text-xs">
                  <Lock className="w-4 h-4 text-amber-400" />
                  One-Time Submission
                </div>
              )}
            </div>
          </div>
        </FadeInUp>

        {/* Fixed Toast Notification */}
        <div className="fixed top-24 right-4 z-[9999]">
          {bannerMessage.text && (
            <div className={`p-4 rounded-xl shadow-lg border flex items-center gap-3 animate-[fadeInUp_0.3s_ease_forwards] ${
              bannerMessage.type === "success"
                ? "bg-emerald-950 border-emerald-800 text-emerald-100"
                : "bg-rose-950 border-rose-800 text-rose-100"
            }`}>
              {bannerMessage.type === "success" ? (
                <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
              )}
              <span className="text-sm font-medium">{bannerMessage.text}</span>
            </div>
          )}
        </div>

        {/* Main Onboarding Form */}
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <StaggerContainer>
            {/* Section 1: General Identity */}
            <FadeInUp delay={100}>
              <div className={`${isDark ? "bg-[#161b22]" : "bg-white"} border ${t.border} rounded-xl p-6 shadow-md card-hover`}>
                <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-indigo-400" /> Official Identity & Work Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Legal Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isLocked}
                      required
                      placeholder="e.g. Alex Morgan"
                      className={getInputClass("fullName")}
                    />
                    {fieldErrors.fullName && <p className="text-xs text-rose-500 mt-1">{fieldErrors.fullName}</p>}
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Employee Badge ID *</label>
                    <input
                      type="text"
                      name="employeeId"
                      value={formData.employeeId}
                      onChange={handleInputChange}
                      disabled={isLocked}
                      required
                      placeholder="WC-8492"
                      className={`${getInputClass("employeeId")} font-mono`}
                    />
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Designation / Role *</label>
                    <input
                      type="text"
                      name="jobTitle"
                      value={formData.jobTitle}
                      onChange={handleInputChange}
                      disabled={isLocked}
                      required
                      placeholder="Fleet Engineer / Dispatcher"
                      className={getInputClass("jobTitle")}
                    />
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Department *</label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      disabled={isLocked}
                      required
                      placeholder="Self-Drive Operations"
                      className={getInputClass("department")}
                    />
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Primary Fleet Hub Location *</label>
                    <input
                      type="text"
                      name="fleetHub"
                      value={formData.fleetHub}
                      onChange={handleInputChange}
                      disabled={isLocked}
                      required
                      placeholder="Bengaluru Central Station"
                      className={getInputClass("fleetHub")}
                    />
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Joining Date *</label>
                    <input
                      type="date"
                      name="joinDate"
                      value={formData.joinDate}
                      onChange={handleInputChange}
                      disabled={isLocked}
                      required
                      className={getInputClass("joinDate")}
                    />
                  </div>
                </div>
              </div>
            </FadeInUp>

            {/* Section 2: Contact & Communications */}
            <FadeInUp delay={200}>
              <div className={`${isDark ? "bg-[#161b22]" : "bg-white"} border ${t.border} rounded-xl p-6 shadow-md card-hover mt-6`}>
                <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" /> Contact & Emergency Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Official Work Email *</label>
                    <input
                      type="email"
                      name="workEmail"
                      value={formData.workEmail}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isLocked}
                      required
                      placeholder="alex@wowcarz.in"
                      className={getInputClass("workEmail")}
                    />
                    {fieldErrors.workEmail && <p className="text-xs text-rose-500 mt-1">{fieldErrors.workEmail}</p>}
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Personal Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isLocked}
                      required
                      placeholder="+91 98765 43210"
                      className={getInputClass("phone")}
                    />
                    {!isLocked && phoneStatus === 'checking' && <p className="text-[11px] text-amber-500 mt-1">Verifying uniqueness...</p>}
                    {!isLocked && phoneStatus === 'available' && <p className="text-[11px] text-emerald-500 mt-1">Phone number is unique.</p>}
                    {!isLocked && phoneStatus === 'taken' && <p className="text-[11px] text-rose-500 mt-1">This number is already registered.</p>}
                    {fieldErrors.phone && <p className="text-[11px] text-rose-500 mt-1">{fieldErrors.phone}</p>}
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Emergency Contact Person *</label>
                    <input
                      type="text"
                      name="emergencyContactName"
                      value={formData.emergencyContactName}
                      onChange={handleInputChange}
                      disabled={isLocked}
                      required
                      placeholder="Contact Name & Relation"
                      className={getInputClass("emergencyContactName")}
                    />
                  </div>
                </div>
              </div>
            </FadeInUp>

            {/* Section 2b: Personal Details & Account Credentials */}
            <FadeInUp delay={250}>
              <div className={`${isDark ? "bg-[#161b22]" : "bg-white"} border ${t.border} rounded-xl p-6 shadow-md card-hover mt-6`}>
                <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
                  <UserCircle2 className="w-4 h-4 text-indigo-400" /> Personal Details & Login Credentials
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Gender *</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      disabled={isLocked}
                      required
                      className={getInputClass("gender")}
                    >
                      <option value="" disabled>Select gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Date of Birth *</label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isLocked}
                      required
                      className={getInputClass("dob")}
                    />
                    {fieldErrors.dob && <p className="text-xs text-rose-500 mt-1">{fieldErrors.dob}</p>}
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Personal Email ID *</label>
                    <input
                      type="email"
                      name="personalEmail"
                      value={formData.personalEmail}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isLocked}
                      required
                      placeholder="alex.personal@gmail.com"
                      className={getInputClass("personalEmail")}
                    />
                    {fieldErrors.personalEmail && <p className="text-xs text-rose-500 mt-1">{fieldErrors.personalEmail}</p>}
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Username *</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isLocked}
                      required
                      autoComplete="off"
                      placeholder="e.g. alex.morgan"
                      className={getInputClass("username")}
                    />
                    {!isLocked && usernameStatus === 'checking' && <p className="text-[11px] text-amber-500 mt-1">Checking availability...</p>}
                    {!isLocked && usernameStatus === 'available' && <p className="text-[11px] text-emerald-500 mt-1">Username available</p>}
                    {!isLocked && usernameStatus === 'taken' && <p className="text-[11px] text-rose-500 mt-1">Username taken</p>}
                    {fieldErrors.username && <p className="text-[11px] text-rose-500 mt-1">{fieldErrors.username}</p>}
                  </div>

                  <div>
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Password *</label>
                    {isLocked ? (
                      <div className={`${getInputClass("password")} flex items-center gap-2 tracking-widest select-none`}>
                        <KeyRound className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                        ••••••••••••
                      </div>
                    ) : (
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isLocked}
                          required
                          autoComplete="new-password"
                          placeholder="Create a login password"
                          className={`${getInputClass("password")} pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 ${t.textFaint} hover:${t.text} transition-colors`}
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    )}
                    {!isLocked && (
                        <>
                            <div className="flex h-1.5 mt-2 rounded-full bg-slate-200/20 overflow-hidden">
                                <div 
                                    className={`h-full ${passwordStrength.color} transition-all duration-300`} 
                                    style={{ width: `${(passwordStrength.score / 5) * 100}%` }} 
                                />
                            </div>
                            <p className="text-[11px] mt-1 text-slate-500 flex justify-between">
                                <span>{passwordStrength.label}</span>
                                {fieldErrors.password && <span className="text-rose-500">{fieldErrors.password}</span>}
                            </p>
                        </>
                    )}
                  </div>

                  {!isLocked && (
                      <div>
                        <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Confirm Password *</label>
                        <div className="relative">
                            <input
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            onBlur={(e) => setFieldErrors(prev => ({...prev, confirmPassword: e.target.value !== formData.password ? "Passwords don't match." : ""}))}
                            required
                            autoComplete="new-password"
                            placeholder="Repeat password"
                            className={`${getInputClass("confirmPassword")} pr-10`}
                            />
                            <button
                            type="button"
                            onClick={() => setShowConfirmPassword((v) => !v)}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 ${t.textFaint} hover:${t.text} transition-colors`}
                            tabIndex={-1}
                            >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {fieldErrors.confirmPassword && <p className="text-[11px] text-rose-500 mt-1">{fieldErrors.confirmPassword}</p>}
                      </div>
                  )}
                </div>
              </div>
            </FadeInUp>

            {/* Section 2c: Roles & Access (Read-only after submission) */}
            {isLocked && (
              <FadeInUp delay={275}>
                <div className={`${isDark ? "bg-[#161b22]" : "bg-white"} border ${t.border} rounded-xl p-6 shadow-md card-hover mt-6`}>
                  <h2 className="text-base font-semibold mb-1 flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-400" /> Roles & Access
                  </h2>
                  <p className={`text-xs ${t.textMuted} mb-4`}>
                    Assigned by system administrators.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className={`text-xs font-medium ${t.textFaint} uppercase tracking-wider mb-2 flex items-center gap-1.5`}>
                        <ShieldCheck className="w-3.5 h-3.5" /> Roles
                      </h3>
                      {accessInfo.roles.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {accessInfo.roles.map((role) => (
                            <Chip
                              key={role}
                              label={role}
                              size="small"
                              className="bg-indigo-950 text-indigo-300 border border-indigo-800 text-xs font-medium"
                            />
                          ))}
                          {accessInfo.level && (
                            <Chip
                              label={`Level: ${accessInfo.level}`}
                              size="small"
                              className={`${isDark ? "bg-slate-800 text-slate-300 border-slate-700" : "bg-slate-100 text-slate-600 border-slate-300"} font-mono text-xs border`}
                            />
                          )}
                        </div>
                      ) : (
                        <p className={`text-xs ${t.textFaint}`}>No roles assigned yet.</p>
                      )}
                    </div>

                    <div>
                      <h3 className={`text-xs font-medium ${t.textFaint} uppercase tracking-wider mb-2 flex items-center gap-1.5`}>
                        <Settings2 className="w-3.5 h-3.5" /> Permitted Actions
                      </h3>
                      {accessInfo.actions.length > 0 ? (
                        <ul className="space-y-1.5">
                          {accessInfo.actions.map((action) => (
                            <li key={action} className="flex items-center gap-2 text-xs">
                              <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                              <span className={t.textMuted}>{action}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className={`text-xs ${t.textFaint}`}>No actions configured yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              </FadeInUp>
            )}

            {/* Section 3: Compliance Documents & Extra Info */}
            <FadeInUp delay={300}>
              <div className={`${isDark ? "bg-[#161b22]" : "bg-white"} border ${t.border} rounded-xl p-6 shadow-md card-hover mt-6`}>
                <h2 className="text-base font-semibold mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-indigo-400" /> Compliance & Identity Attachments
                </h2>
                <p className={`text-xs ${t.textMuted} mb-6`}>
                  Upload scanned PDF or JPG copies of your government and onboarding documents.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6 pb-6 border-b border-slate-700/50">
                    <div>
                        <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Mode of Transport *</label>
                        <select
                            name="modeOfTransport"
                            value={formData.modeOfTransport}
                            onChange={handleInputChange}
                            disabled={isLocked}
                            required
                            className={getInputClass("modeOfTransport")}
                        >
                            <option value="" disabled>Select Transport Mode</option>
                            <option value="Own Vehicle">Own Vehicle</option>
                            <option value="Company Transport">Company Transport</option>
                            <option value="Public Transit">Public Transit</option>
                        </select>
                    </div>
                    <div>
                        <label className={`block text-xs font-medium ${t.textMuted} mb-1.5`}>Primary ID Proof *</label>
                        <select
                            name="idProof"
                            value={formData.idProof}
                            onChange={handleInputChange}
                            disabled={isLocked}
                            required
                            className={getInputClass("idProof")}
                        >
                            <option value="" disabled>Select ID Proof</option>
                            <option value="Aadhaar Card">Aadhaar Card</option>
                            <option value="PAN Card">PAN Card</option>
                            <option value="Passport">Passport</option>
                            <option value="Voter ID">Voter ID</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Document 1 */}
                  <div className={`border border-dashed ${t.borderInput} ${isDark ? "bg-[#0d1117]" : "bg-slate-50"} rounded-xl p-4 flex flex-col items-center justify-center text-center hover:border-indigo-500/60 transition-colors`}>
                    <UploadCloud className="w-8 h-8 text-indigo-400 mb-2" />
                    <p className="text-xs font-medium">Commercial / Driving License</p>
                    <p className={`text-[11px] ${t.textFaint} mt-0.5`}>PDF or PNG up to 5MB</p>
                    {documents.drivingLicense ? (
                      <span className="text-xs text-emerald-400 mt-2 font-mono truncate max-w-[200px]">
                        ✓ {documents.drivingLicense.name}
                      </span>
                    ) : isLocked ? (
                      <span className={`text-xs ${t.textFaint} mt-2`}>Document on file</span>
                    ) : (
                      <label className={`mt-3 cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-600" : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300"} border transition`}>
                        Browse File
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleDocumentChange("drivingLicense", e)}
                        />
                      </label>
                    )}
                  </div>

                  {/* Document 2 */}
                  <div className={`border border-dashed ${t.borderInput} ${isDark ? "bg-[#0d1117]" : "bg-slate-50"} rounded-xl p-4 flex flex-col items-center justify-center text-center hover:border-indigo-500/60 transition-colors`}>
                    <UploadCloud className="w-8 h-8 text-indigo-400 mb-2" />
                    <p className="text-xs font-medium">Government ID Document</p>
                    <p className={`text-[11px] ${t.textFaint} mt-0.5`}>PDF or PNG up to 5MB</p>
                    {documents.aadhaarPan ? (
                      <span className="text-xs text-emerald-400 mt-2 font-mono truncate max-w-[200px]">
                        ✓ {documents.aadhaarPan.name}
                      </span>
                    ) : isLocked ? (
                      <span className={`text-xs ${t.textFaint} mt-2`}>Document on file</span>
                    ) : (
                      <label className={`mt-3 cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-600" : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300"} border transition`}>
                        Browse File
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handleDocumentChange("aadhaarPan", e)}
                        />
                      </label>
                    )}
                  </div>

                  {/* Document 3 */}
                  <div className={`border border-dashed ${t.borderInput} ${isDark ? "bg-[#0d1117]" : "bg-slate-50"} rounded-xl p-4 flex flex-col items-center justify-center text-center hover:border-indigo-500/60 transition-colors`}>
                    <UploadCloud className="w-8 h-8 text-indigo-400 mb-2" />
                    <p className="text-xs font-medium">Signed Employment Agreement</p>
                    <p className={`text-[11px] ${t.textFaint} mt-0.5`}>PDF up to 10MB</p>
                    {documents.resumeAgreement ? (
                      <span className="text-xs text-emerald-400 mt-2 font-mono truncate max-w-[200px]">
                        ✓ {documents.resumeAgreement.name}
                      </span>
                    ) : isLocked ? (
                      <span className={`text-xs ${t.textFaint} mt-2`}>Document on file</span>
                    ) : (
                      <label className={`mt-3 cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-600" : "bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-300"} border transition`}>
                        Browse File
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf"
                          onChange={(e) => handleDocumentChange("resumeAgreement", e)}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-700/50">
                    <label className={`block text-xs font-medium ${t.textMuted} mb-1.5 flex items-center gap-1.5`}>
                        <Info className="w-3.5 h-3.5" /> Additional Information (Optional)
                    </label>
                    <textarea 
                        name="otherInfo" 
                        value={formData.otherInfo} 
                        onChange={handleInputChange}
                        disabled={isLocked}
                        placeholder="Any other details HR should be aware of..." 
                        className={`${getInputClass("otherInfo")} min-h-[100px] resize-y`}
                    />
                </div>
              </div>
            </FadeInUp>
          </StaggerContainer>

          {/* Sticky Submit Bar on Mobile / Bottom CTA */}
          {!isLocked && (
            <FadeInUp delay={400}>
              <div className={`fixed bottom-0 left-0 w-full p-4 border-t z-50 flex items-center justify-between ${isDark ? 'bg-[#0d1117]/95 border-slate-800' : 'bg-white/95 border-slate-200'} backdrop-blur-lg sm:static sm:bg-transparent sm:border-t-0 sm:p-0 sm:justify-end mt-6`}>
                {submitting && uploadProgress > 0 && (
                  <div className="w-1/3 mr-4">
                     <p className="text-[10px] text-indigo-400 mb-1">Uploading... {uploadProgress}%</p>
                     <LinearProgress variant="determinate" value={uploadProgress} sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#6366f1' }, backgroundColor: 'rgba(99, 102, 241, 0.2)' }} />
                  </div>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting || phoneStatus === 'taken' || usernameStatus === 'taken'}
                  className="bg-indigo-600 hover:bg-indigo-500 text-white font-medium px-8 py-3 rounded-xl shadow-lg shadow-indigo-600/30 capitalize text-sm transition hover:scale-[1.02] ml-auto"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2"><CircularProgress size={16} color="inherit" /> Submitting...</span>
                  ) : (
                    "Submit & Permanently Lock Profile"
                  )}
                </Button>
              </div>
            </FadeInUp>
          )}
        </form>
      </div>
    </div>
  );
}